/**
 * Vercel AI SDK adapter for edit_file tool
 */

import { tool } from 'ai';
import { z } from 'zod';
import { executeEditFile } from './core.js';
import { EDIT_FILE_TOOL_DESCRIPTION, EDIT_FILE_SYSTEM_PROMPT } from './prompts.js';
import type { EditFileConfig } from './types.js';

/**
 * Vercel AI SDK tool for edit_file
 * 
 * @example
 * ```ts
 * import { generateText } from 'ai';
 * import { anthropic } from '@ai-sdk/anthropic';
 * import { editFileTool } from 'morphsdk/tools/vercel';
 * 
 * const result = await generateText({
 *   model: anthropic('claude-sonnet-4-5-20250929'),
 *   tools: { editFile: editFileTool },
 *   prompt: "Fix the bug in app.ts"
 * });
 * ```
 */
const editFileSchema = z.object({
  target_filepath: z.string().describe('The path of the target file to modify'),
  instruction: z
    .string()
    .describe('A single sentence written in the first person describing what the agent is changing. Used to help disambiguate uncertainty in the edit.'),
  code_edit: z
    .string()
    .describe('Specify ONLY the precise lines of code that you wish to edit. Use `// ... existing code ...` for unchanged sections.'),
});

export const editFileTool = tool({
  description: EDIT_FILE_TOOL_DESCRIPTION,
  inputSchema: editFileSchema,
  execute: async (params) => {
    const result = await executeEditFile({
      target_filepath: params.target_filepath,
      instruction: params.instruction,
      code_edit: params.code_edit,
    });
    
    if (!result.success) {
      throw new Error(`Failed to edit file: ${result.error}`);
    }
    
    return {
      success: true,
      filepath: result.filepath,
      changes: result.changes,
      udiff: result.udiff,
    };
  },
});

/**
 * Get the system prompt for edit_file usage
 * 
 * Add this to your system message to guide the model on using edit_file properly.
 * 
 * @example
 * ```ts
 * const result = await generateText({
 *   model: anthropic('claude-sonnet-4-5-20250929'),
 *   system: getSystemPrompt(),
 *   tools: { editFile: editFileTool },
 *   prompt: "Fix bugs"
 * });
 * ```
 */
export function getSystemPrompt(): string {
  return EDIT_FILE_SYSTEM_PROMPT;
}

/**
 * Create a custom edit_file tool with configuration
 * 
 * @param config - Configuration options
 * @returns Vercel AI SDK tool with custom config
 * 
 * @example
 * ```ts
 * const customEditTool = createEditFileTool({
 *   baseDir: './src',
 *   generateUdiff: true,
 *   description: 'Custom tool description for your use case'
 * });
 * 
 * const result = await generateText({
 *   model: anthropic('claude-sonnet-4-5-20250929'),
 *   tools: { editFile: customEditTool },
 *   prompt: "Fix bugs"
 * });
 * ```
 */
export function createEditFileTool(config: EditFileConfig = {}) {
  const schema = z.object({
    target_filepath: z.string().describe('The path of the target file to modify'),
    instruction: z
      .string()
      .describe('A single sentence written in the first person describing what the agent is changing. Used to help disambiguate uncertainty in the edit.'),
    code_edit: z
      .string()
      .describe('Specify ONLY the precise lines of code that you wish to edit. Use `// ... existing code ...` for unchanged sections.'),
  });

  return tool({
    description: config.description || EDIT_FILE_TOOL_DESCRIPTION,
    inputSchema: schema,
    execute: async (params) => {
      const result = await executeEditFile(
        {
          target_filepath: params.target_filepath,
          instruction: params.instruction,
          code_edit: params.code_edit,
        },
        config
      );
      
      if (!result.success) {
        throw new Error(`Failed to edit file: ${result.error}`);
      }
      
      return {
        success: true,
        filepath: result.filepath,
        changes: result.changes,
        udiff: result.udiff,
      };
    },
  });
}

// Default export for convenience
export default editFileTool;

